import type { CardProps } from '@mantine/core';
import { useCallback } from 'react';

import { getVisibleUserEntriesCount } from '~/domains/contest/domains/common/utils/pickControl';
import useRedirectToEO from '~/domains/common/utils/useRedirectToEO';

import { useServiceFeeModalHandler } from '../../hooks/useServiceFeeModalHandler';
import useGamecenterPath from '../../../gamecenter/domains/common/hooks/useGamecenterPath';
import { getContestNavigationTarget } from '../../utils/getContestNavigationTarget';

import { ContestCardFrame } from './ContestCardFrame';
import type { ContestCardProps } from './types';

interface CardContainerProps extends CardProps, ContestCardProps {
  /**
   * Controls whether navigation should be blocked for contests with service fee issues.
   * When true, contests with overdue or cancelled service fees will open a modal
   * instead of navigating to the contest page.
   *
   * @default false
   * @example
   * // Block navigation for user's own contests with service fee issues
   * <CardContainer shouldBlockForServiceFee={true} contest={userContest} />
   *
   * // Allow normal navigation (e.g., for public contest discovery)
   * <CardContainer shouldBlockForServiceFee={false} contest={publicContest} />
   */
  shouldBlockForServiceFee?: boolean;
}

function CardContainer({
  contest,
  children,
  onClick,
  isStatic,
  promoSource,
  promoIndex,
  currentSlateStatus,
  shouldBlockForServiceFee = false,
  ...paperProps
}: CardContainerProps) {
  const { shouldBlockNavigation, handleCardClick: handleServiceFeeClick } =
    useServiceFeeModalHandler(contest, shouldBlockForServiceFee);
  const { redirectToEO } = useRedirectToEO();

  const navTarget = getContestNavigationTarget(contest);
  const isOfpEo = navTarget.kind === 'eo' && navTarget.brand?.toUpperCase() === 'OFP';

  const baseContestPath = useGamecenterPath({
    contestId: contest.id,
    contestType: contest.contest_type,
    sport: contest.league?.sport,
    contestStatus: contest.status,
    userEntriesCount: getVisibleUserEntriesCount(contest),
    promoSource,
    promoIndex,
    currentSlateStatus,
  });

  // Navigation is driven by the URL the BE returns, not the contest source.
  // splashV2 and non-OFP eo (ryp) resolve to the external game-center URL via a plain
  // link (full-page nav, no token handoff). OFP navigates imperatively on click instead
  // (see ofpRedirectUrl) because it needs a single-use token appended at click time.
  // internal uses the in-app route. `none` (an external contest with no URL) is
  // intentionally not linkable — never fall back to the internal route.
  const contestPath = (() => {
    if (shouldBlockNavigation || isStatic) return null;
    switch (navTarget.kind) {
      case 'eo':
        return isOfpEo ? null : navTarget.url;
      case 'splashV2':
        return navTarget.url;
      case 'internal':
        return baseContestPath;
      default:
        return null;
    }
  })();

  // OFP game-center links carry a single-use token appended at click time, so they can't
  // be a declarative href — they navigate through redirectToEO instead.
  const ofpRedirectUrl = isOfpEo && !shouldBlockNavigation && !isStatic ? navTarget.url : null;

  const handleClick = useCallback(
    (e) => {
      // Dropdown menu / nested links should not trigger card navigation.
      const ignoreLinkContainer = e.currentTarget.querySelector('[data-ignore-link="true"]');
      if (
        e.target !== e.currentTarget &&
        (ignoreLinkContainer?.contains(e.target) || e.target?.tagName === 'A')
      ) {
        e.preventDefault();
      }

      // Service fee modal takes precedence over navigation.
      const serviceFeeModalOpened = handleServiceFeeClick(e);
      if (serviceFeeModalOpened) {
        return;
      }

      // OFP: append a single-use token and full-page navigate (no declarative href).
      if (ofpRedirectUrl) {
        e.preventDefault();
        redirectToEO(ofpRedirectUrl, false, true);
        return;
      }

      onClick?.(e);
    },
    [handleServiceFeeClick, onClick, ofpRedirectUrl, redirectToEO]
  );

  return (
    <ContestCardFrame
      {...paperProps}
      href={contestPath}
      onClick={handleClick}
      sx={{
        border: 'none',
        cursor: isStatic && !onClick ? 'default' : 'pointer',
      }}
    >
      {children}
    </ContestCardFrame>
  );
}

export default CardContainer;
